import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	Box,
	Button,
	Divider,
	InputLabel,
	Menu,
	MenuDropdown,
	MenuOption,
	MenuOptionList,
	PopoverTrigger,
	Select,
	SelectOption
} from "@launchmetrics/lm-ui";

import { useFeedFinder } from "../hooks";
import { FeedObjectFocusName } from "class/Feed";
import { PLACEMENTS_COUNT } from "./consts";
import { FeedClippingCount, ReportFeedClipping, removeReportClipping, setReportClipping } from "store/article/report/reducers";

import { ReactComponent as DeleteClipping } from 'vendor/svg/report/delete-clipping.svg';

export const FeedClipping: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const filterFeedIds = useSelector((state: any) => state.search.filters.feeds);
	const feedClippings = useSelector((state: any) => state.report?.configuration.definition.settings?.feedClippings);

	const filterFeeds = useFeedFinder(filterFeedIds);

	const [openFeedClippingDropdown, setOpenFeedClippingDropdown] = useState(false);
	const [feedClippingSearch, setFeedClippingSearch] = useState('');
	const [selectedFeedClippingIds, setSelectedFeedClippingIds] = useState<string[]>([]);
	const selectedFeeds = useFeedFinder(selectedFeedClippingIds);

	const setClipping = useCallback((feedId: string, count: FeedClippingCount) => {
		dispatch(setReportClipping({feedId, count}));
	}, [dispatch]);

	const onToggleFeedClippingSelection = useCallback((feedId: string) => {
		const selectedIndex = selectedFeedClippingIds.indexOf(feedId);
		let newSelectedFeedIds;
		if (selectedIndex > -1) {
			newSelectedFeedIds = selectedFeedClippingIds.filter(id => id !== feedId);
			dispatch(removeReportClipping({feedId}));
		}
		else newSelectedFeedIds = [...selectedFeedClippingIds, feedId];
		setSelectedFeedClippingIds(newSelectedFeedIds);
	}, [selectedFeedClippingIds, dispatch, setSelectedFeedClippingIds]);

	const feedsByFocus = useMemo(() => {
		return filterFeeds
			.filter(feed => !(!isEmpty(feedClippingSearch) && !feed.name.includes(feedClippingSearch)))
			.reduce((acc: {[focusName: string]: FeedObjectFocusName[]}, feed) => {
				if (!acc[feed.focusName]) acc[feed.focusName] = [];
				acc[feed.focusName].push(feed);
				return acc;
			}, {});
	}, [filterFeeds, feedClippingSearch]);

	const feedClippingsSelector = useMemo(() => {
		return (
			<Menu
				onChange={() => {}}
				onSearchInputChange={search => setFeedClippingSearch(search)}
				popoverProps={{placement: 'left-start'}}
				onOpenChange={() => setOpenFeedClippingDropdown(!openFeedClippingDropdown)}
				open={openFeedClippingDropdown}
				selectedValues={selectedFeedClippingIds}
			>
				<PopoverTrigger>
					<Button onClick={() => setOpenFeedClippingDropdown(!openFeedClippingDropdown)} variant="tertiary">
						+ ADD
					</Button>
				</PopoverTrigger>
				<MenuDropdown>
					<MenuOptionList>
						{Object.keys(feedsByFocus).map((focusName, focusIndex) => (
							<>
								<Box key={focusIndex} style={{marginLeft: '10px'}}>{focusName}</Box>
								{feedsByFocus[focusName].map((feed, feedIndex) => (
									<MenuOption
										key={feedIndex}
										value={feed.id}
										onClick={() => onToggleFeedClippingSelection(feed.id)}
									>
										{feed.name}
									</MenuOption>	
								))}
								<Divider />
							</>
						))}
					</MenuOptionList>
				</MenuDropdown>
			</Menu>
		);
	}, [
		selectedFeedClippingIds, feedsByFocus, openFeedClippingDropdown,
		onToggleFeedClippingSelection, setFeedClippingSearch, setOpenFeedClippingDropdown
	]);

	const feedClippingsConfiguration = useMemo(() => {
		return (<>
			{selectedFeeds && selectedFeeds.map((feed, index) => (
				<Box className="side-panel-inline" key={index}>
					<InputLabel>{feed.name}</InputLabel>
					<Select
						placeholder={t('reports.content_text') as string}
						defaultValue={{ key: '1', value: '10' }}
					>
						{PLACEMENTS_COUNT.map((count, index) => (
							<SelectOption
								selected={feedClippings?.find((clipping: ReportFeedClipping) => clipping.id === feed.id)?.count === count}
								key={index}
								onClick={() => setClipping(feed.id, count as FeedClippingCount)}
								optionValue={{
									key: count.toString(),
									value: count.toString()
								}}
							/>
						))}
					</Select>
					<DeleteClipping onClick={() => onToggleFeedClippingSelection(feed.id)} />
				</Box>
			))}
		</>);
	}, [selectedFeeds, feedClippings, onToggleFeedClippingSelection, setClipping, t]);

	return (
		<>
			<Box className='side-panel-inline'>
				<InputLabel>{t('Include clipping of first clippings')}</InputLabel>
				{feedClippingsSelector}
			</Box>
			<Box className='side-panel-content-entry-element'>
				{feedClippingsConfiguration}
			</Box>
		</>
	);
};
